import { Box } from "@mui/material";
import Masonry from "@mui/lab/Masonry";
import Button from "@mui/material/Button";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import * as React from "react";
import { PageData } from "../interfaces";

import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Captions from "yet-another-react-lightbox/plugins/captions";
import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/captions.css";
import Grid from "@mui/material/Grid";

interface PropType {
  data: PageData;
}

interface StateType {
  loadState: String;
  modalOpen: Boolean;
  selectionIndex: Number;
  descriptionExpanded: Boolean;
}

interface GalImageData {
  img: string;
  thumbnail: string;
  title: string;
  date: string;
  description: string;
  thumbWidth: number;
  thumbHeight: number;
}

class LytGalleryPage extends React.Component<PropType, StateType> {
  state = {
    loadState: "loading",
    modalOpen: false,
    selectionIndex: 0,
    descriptionExpanded: false,
  };

  galleryData?: Array<GalImageData>;
  descMem: String = "";

  componentDidMount = () => {
    this.getGalleryData();
  };

  componentDidUpdate(prevProps: PropType): void {
    if (prevProps.data.dataSrc !== this.props.data.dataSrc) {
      this.setState({ loadState: "loading" }, () => {
        this.getGalleryData();
      });
    }
  }

  forceMasonryRefresh = () => {
    const masonElem = document.querySelector(".masonGallery");
    if (masonElem && masonElem.parentElement) {
      const parent = masonElem.parentElement;
      parent.style.width = "99%";
      setTimeout(() => {
        parent.style.width = "100%";
        (parent.firstElementChild as HTMLElement).style.opacity = "1";
      }, 50);
    }
  };

  getGalleryData = () => {
    return new Promise((resolve, reject) => {
      fetch(this.props.data.dataSrc as string)
        .then((response) => response.json())
        .then((galDat: Array<GalImageData>) => {
          this.galleryData = galDat;
          this.setState(
            {
              loadState: "loaded",
            },
            () => {
              this.forceMasonryRefresh();
              resolve(this.state.loadState);
            }
          );
        });
    });
  };

  setSelectionIndex = (setTo: Number) => {
    this.setState({
      selectionIndex: setTo,
    });
  };

  setModalState = (setTo: boolean) => {
    this.setState({
      modalOpen: setTo,
    });
  };

  setDescriptionExpantionState = (setTo: boolean) => {
    this.setState({
      descriptionExpanded: setTo,
    });
  };

  render() {
    const BuildLoadState = () => {
      if (this.state.loadState === "loaded") {
        return <div id="statePage">{BuildLoadedState()}</div>;
      }
      return <div id="statePage">{BuildLoadingState()}</div>;
    };

    const BuildLoadingState = () => {
      return <div>loading images..</div>;
    };

    const BuildDesctionContent = (descText: string, dateText: string) => {
      return (
        <Grid container sx={{ justifyContent: "center" }}>
          <Grid size={{ xs: 12, md: 10, lg: 8 }}>
            <Box
              style={{
                marginBottom: "30px",
              }}
            >
              <div
                className="galDescriptionText"
                style={{
                  maxHeight: this.state.descriptionExpanded ? "500px" : "40px",
                }}
              >
                <div
                  style={{
                    maxHeight: "100%", // internal scrolling
                  }}
                >
                  <div dangerouslySetInnerHTML={{ __html: descText }}></div>
                  <Box>
                    <CalendarMonthIcon style={{ verticalAlign: "middle" }} />
                    <span>
                      <strong>{dateText}</strong>
                    </span>
                  </Box>
                </div>
              </div>
              <Button
                onClick={() => {
                  this.setDescriptionExpantionState(
                    !this.state.descriptionExpanded
                  );
                }}
                size="small"
                variant="text"
                sx={{
                  marginTop: 1,
                  textTransform: "none",
                  fontSize: "0.9rem",
                  color: "primary.main",
                }}
              >
                {this.state.descriptionExpanded ? "Show Less" : "Read More"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      );
    };

    const BuildLoadedState = () => {
      if (!this.galleryData || !this.galleryData[this.state.selectionIndex]) {
        return <p>Image load error</p>;
      }
      return (
        <Box>
          <Lightbox
            open={this.state.modalOpen}
            close={() => this.setModalState(false)}
            plugins={[Zoom, Captions]}
            render={{
              buttonPrev: () => null,
              buttonNext: () => null,
            }}
            slides={[
              {
                src: this.galleryData[this.state.selectionIndex].img,
                alt: this.galleryData[this.state.selectionIndex].title,
                title: this.galleryData[this.state.selectionIndex].title,
                description: BuildDesctionContent(
                  this.galleryData[this.state.selectionIndex].description,
                  this.galleryData[this.state.selectionIndex].date
                ),
              },
            ]}
            carousel={{
              finite: true,
              preload: 0,
            }}
            animation={{
              swipe: 0,
            }}
          />
          <Box sx={{ width: "100%" }}>
            <Masonry
              className="masonGallery"
              columns={{ xs: 2, sm: 3 }}
              spacing={{ xs: 0.3, sm: 1 }}
              style={{ opacity: 0.01 }}
            >
              {this.galleryData.map((item, index) => {
                const aspectRatio = (item.thumbHeight / item.thumbWidth) * 100;
                return (
                  <Button
                    key={index}
                    onClick={() => {
                      this.setModalState(true);
                      this.setSelectionIndex(index);
                    }}
                    tabIndex={index + 1}
                    sx={{ p: 0 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        paddingTop: `${aspectRatio}%`, // maintain aspect ratio
                        overflow: "hidden",
                      }}
                    ></Box>

                    <img
                      srcSet={`${item.thumbnail}?&auto=format&dpr=2 2x`}
                      src={`${item.thumbnail}?&auto=format`}
                      alt={item.title.toString()}
                      loading="lazy"
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </Button>
                );
              })}
            </Masonry>
          </Box>
        </Box>
      );
    };

    return <Box id="lytGalleryPageWrapper">{BuildLoadState()}</Box>;
  }
}

export default LytGalleryPage;
